import { useState, useRef, useEffect } from "react";

const AI_API = "https://bn360.bluenett.solutions/ai-api";  // Apache proxy to .33:8600

const SUGGESTIONS = [
  "Which server is free for a new app?",
  "How many python scripts run on the device server?",
  "Show me the status of all servers",
  "Check docker errors on the docker server",
];

export default function AIAssistantPanel({ token, theme }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => "s_" + Math.random().toString(36).slice(2, 10));
  const endRef = useRef(null);
  const inputRef = useRef(null);

  const bg = theme?.bg || "#0f172a";
  const card = theme?.bgCard || "#1e293b";
  const border = theme?.border || "rgba(148,163,184,0.15)";
  const txt = theme?.text || "#e2e8f0";
  const muted = theme?.textMuted || "#94a3b8";
  const accent = "#6366f1";

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const send = async (text) => {
    const q = (text ?? input).trim();
    if (!q || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const r = await fetch(`${AI_API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { "X-Auth-Token": token } : {}) },
        body: JSON.stringify({ message: q, session_id: sessionId }),
      });
      const d = await r.json();
      setMessages((m) => [...m, { role: "assistant", content: d.answer || "No response.", tools: d.tools_used || [] }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Could not reach the AI service. Please check the connection.", error: true }]);
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", height: "100%", background: bg, fontFamily: "'Inter',sans-serif", minHeight: 0 }}>
      {/* Header */}
      <div style={{ padding: "16px 24px", borderBottom: `1px solid ${border}`, display: "flex", alignItems: "center", gap: 12, flexShrink: 0 }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: `linear-gradient(135deg, ${accent}, #8b5cf6)`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>🤖</div>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: txt }}>BN360 Assistant</div>
          <div style={{ fontSize: 11, color: muted }}>Ask anything about your infrastructure</div>
        </div>
        <div style={{ marginLeft: "auto", fontSize: 10, color: muted, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#1D9E75", display: "inline-block" }} /> Live
        </div>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: "auto", padding: "24px", minHeight: 0 }}>
        {messages.length === 0 && (
          <div style={{ maxWidth: 620, margin: "40px auto 0", textAlign: "center" }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>🤖</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: txt, marginBottom: 8 }}>How can I help with your servers?</div>
            <div style={{ fontSize: 13, color: muted, marginBottom: 28 }}>Ask in plain language. I read live data from your servers to answer.</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => send(s)} style={{
                  textAlign: "left", padding: "14px 16px", borderRadius: 12, cursor: "pointer",
                  background: card, border: `1px solid ${border}`, color: txt, fontSize: 13, fontFamily: "'Inter',sans-serif",
                  transition: "all .15s", lineHeight: 1.4,
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = accent; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = border; }}
                >{s}</button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} style={{ maxWidth: 760, margin: "0 auto 20px", display: "flex", gap: 12, flexDirection: m.role === "user" ? "row-reverse" : "row" }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15,
              background: m.role === "user" ? accent : `linear-gradient(135deg, ${accent}, #8b5cf6)` }}>
              {m.role === "user" ? "👤" : "🤖"}
            </div>
            <div style={{
              padding: "12px 16px", borderRadius: 14, maxWidth: "78%", fontSize: 14, lineHeight: 1.6,
              whiteSpace: "pre-wrap", wordBreak: "break-word",
              background: m.role === "user" ? accent : card,
              color: m.role === "user" ? "#fff" : (m.error ? "#fca5a5" : txt),
              border: m.role === "user" ? "none" : `1px solid ${border}`,
              borderTopRightRadius: m.role === "user" ? 4 : 14, borderTopLeftRadius: m.role === "user" ? 14 : 4,
            }}>
              {m.content}
              {m.tools && m.tools.length > 0 && (
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${border}`, fontSize: 10, color: muted, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <span>🔧 data from:</span>
                  {m.tools.map((t, j) => (
                    <span key={j} style={{ padding: "2px 8px", borderRadius: 4, background: `${accent}18`, color: "#a5b4fc" }}>{typeof t === "string" ? t : t.tool}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div style={{ maxWidth: 760, margin: "0 auto 20px", display: "flex", gap: 12 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, background: `linear-gradient(135deg, ${accent}, #8b5cf6)` }}>🤖</div>
            <div style={{ padding: "14px 18px", borderRadius: 14, background: card, border: `1px solid ${border}`, borderTopLeftRadius: 4, display: "flex", gap: 5, alignItems: "center" }}>
              <span style={{ fontSize: 12, color: muted }}>Checking your servers</span>
              <span className="ai-dot" style={{ animationDelay: "0s" }} />
              <span className="ai-dot" style={{ animationDelay: ".2s" }} />
              <span className="ai-dot" style={{ animationDelay: ".4s" }} />
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div style={{ padding: "16px 24px 20px", borderTop: `1px solid ${border}`, flexShrink: 0 }}>
        <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", gap: 10, alignItems: "flex-end",
          background: card, border: `1px solid ${border}`, borderRadius: 14, padding: "8px 8px 8px 16px" }}>
          <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKey}
            placeholder="Ask about your servers…" rows={1} disabled={loading}
            style={{ flex: 1, resize: "none", border: "none", outline: "none", background: "transparent",
              color: txt, fontSize: 14, fontFamily: "'Inter',sans-serif", maxHeight: 120, lineHeight: 1.5, paddingTop: 6 }} />
          <button onClick={() => send()} disabled={loading || !input.trim()}
            style={{ width: 38, height: 38, borderRadius: 10, border: "none", cursor: loading || !input.trim() ? "not-allowed" : "pointer",
              background: input.trim() && !loading ? accent : border, color: "#fff", fontSize: 16, flexShrink: 0, transition: "all .15s" }}>➤</button>
        </div>
        <div style={{ maxWidth: 760, margin: "8px auto 0", fontSize: 10, color: muted, textAlign: "center" }}>
          BN360 Assistant reads live server data. Answers are based on real metrics.
        </div>
      </div>

      <style>{`
        .ai-dot { width: 6px; height: 6px; border-radius: 50%; background: ${accent}; display: inline-block; animation: aiBounce 1.2s infinite ease-in-out; }
        @keyframes aiBounce { 0%, 60%, 100% { transform: translateY(0); opacity: .4; } 30% { transform: translateY(-5px); opacity: 1; } }
        textarea::placeholder { color: ${muted}; }
      `}</style>
    </div>
  );
}
